import {
  Dialog,
  TextField,
  Switch,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useState, useEffect } from "react";
import { Layers, X } from "lucide-react";

import {
  getPlanFeatureByIdAction,
  updatePlanFeatureAction,
  getPlanFeatureListAction,
} from "../../Actions/PlanFeatureAction";
import { FRONTEND_MESSAGES } from "../../utils/messages";
import "../../components/Common/FormModal.css";
import "./AddEditPlanFeature.css";

const { VALIDATION } = FRONTEND_MESSAGES;

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#22419e" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#22419e",
  },
};

const subFeatureSchema = Yup.object().shape({
  id: Yup.number().nullable(),
  feature_name: Yup.string().trim().required("Feature name is required"),
  price: Yup.number()
    .typeError(VALIDATION.NUMERIC_ONLY("Price"))
    .min(0, "Price must be at least 0")
    .required(VALIDATION.REQUIRED_FIELD("Price")),
  level: Yup.number()
    .typeError(VALIDATION.NUMERIC_ONLY("Level"))
    .min(1, "Level must be at least 1")
    .required(VALIDATION.REQUIRED_FIELD("Level")),
  is_active: Yup.boolean(),
});

const validationSchema = Yup.object({
  portal_name: Yup.string().required(VALIDATION.REQUIRED_FIELD("Portal Name")),
  feature_name: Yup.string()
    .trim()
    .max(255, VALIDATION.MAX_LENGTH("Feature name", 255))
    .required(VALIDATION.REQUIRED_FIELD("Feature name")),
  price: Yup.number()
    .typeError(VALIDATION.NUMERIC_ONLY("Price"))
    .min(0, "Price must be at least 0")
    .required(VALIDATION.REQUIRED_FIELD("Price")),
  level: Yup.number()
    .typeError(VALIDATION.NUMERIC_ONLY("Level"))
    .min(1, "Level must be at least 1")
    .required(VALIDATION.REQUIRED_FIELD("Level")),
  is_active: Yup.boolean(),
  sub_features: Yup.array().of(subFeatureSchema),
});

const AddEditPlanFeature = ({
  open,
  handleClose,
  id,
  refetchPlanList = () => {},
  handleToast = () => {},
}) => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const formik = useFormik({
    initialValues: {
      portal_name: "",
      name: "",
      feature_name: "",
      price: 0,
      level: 1,
      is_active: true,
      sub_features: [],
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const parentPayload = {
          portal_name: values.portal_name,
          name: values.name,
          feature_name: values.feature_name?.trim(),
          price: values.price,
          level: values.level,
          is_active: values.is_active,
        };

        await updatePlanFeatureAction(id, parentPayload);

        for (let sub of values.sub_features) {
          if (sub.id) {
            const subPayload = {
              feature_name: sub.feature_name?.trim(),
              price: sub.price,
              level: sub.level,
              is_active: sub.is_active,
            };
            await updatePlanFeatureAction(sub.id, subPayload);
          }
        }

        const featureMapping = {
          user_limit_type: "user_limit_value",
          message_limit_type: "message_limit_value",
          agent_training_limit_type: "agent_training_limit_value",
        };

        if (featureMapping[values.feature_name]) {
          const limitedFeature = values.sub_features.find(
            (item) => item.name?.toLowerCase() === "limited"
          );

          if (limitedFeature) {
            const allRes = await getPlanFeatureListAction({ limit: "all" });
            const allFeatures = allRes?.data?.data || [];

            const valueFeature = allFeatures.find(
              (item) =>
                item.feature_name === featureMapping[values.feature_name]
            );

            if (valueFeature) {
              await updatePlanFeatureAction(valueFeature.id, {
                price: limitedFeature.price,
              });
            }
          }
        }

        handleToast(
          "success",
          "Plan feature and sub-features updated successfully"
        );
        handleClose();
        refetchPlanList();
      } catch (err) {
        const message =
          err?.data?.message || err?.message || "Something went wrong";
        handleToast("error", message);
        console.error("Error updating plan feature:", err);
      } finally {
        setLoading(false);
      }
    },
  });

  const getAllDescendants = (parentId, allFeatures) => {
    let descendants = [];
    const children = allFeatures.filter((f) => f.parent_id === parentId);
    for (let child of children) {
      descendants.push(child);
      descendants = descendants.concat(getAllDescendants(child.id, allFeatures));
    }
    return descendants;
  };

  useEffect(() => {
    const fetchFeature = async () => {
      if (!id) return;
      setFetching(true);
      try {
        const response = await getPlanFeatureByIdAction(id);
        const data = response?.data?.data || response?.data || {};

        const allRes = await getPlanFeatureListAction({ limit: "all" });
        const allFeatures = allRes?.data?.data || allRes?.data || [];

        const featureMapping = {
          user_limit_type: "user_limit_value",
          message_limit_type: "message_limit_value",
          agent_training_limit_type: "agent_training_limit_value",
        };

        let linkedValueFeature = null;

        if (featureMapping[data.feature_name]) {
          linkedValueFeature = allFeatures.find(
            (item) => item.feature_name === featureMapping[data.feature_name]
          );
        }

        const allDescendants = getAllDescendants(id, allFeatures);

        formik.resetForm({
          values: {
            portal_name: data.portal_name || "",
            name: data.name || "",
            feature_name: data.feature_name || "",
            price: data.price !== undefined ? parseFloat(data.price) : 0,
            level: data.level || 1,
            is_active: data.is_active ?? true,
            sub_features: allDescendants.map((s) => ({
              id: s.id,
              name: s.name,
              feature_name: s.feature_name,
              price:
                linkedValueFeature && s.name?.toLowerCase() === "limited"
                  ? parseFloat(linkedValueFeature.price || 0)
                  : s.price !== undefined
                    ? parseFloat(s.price)
                    : 0,
              level: s.level,
              is_active: s.is_active,
            })),
          },
        });
      } catch (err) {
        console.error("Error fetching plan feature details:", err);
        handleToast("error", "Error loading feature details");
      } finally {
        setFetching(false);
      }
    };
    fetchFeature();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleTextChange = (e) => {
    formik.handleChange(e);
    formik.setFieldTouched(e.target.name, true, false);
  };

  const handleSubFeatureChange = (index, field, value) => {
    formik.setFieldValue(`sub_features[${index}].${field}`, value);
  };

  const displayName = formik.values.name || formik.values.feature_name || "Feature";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ className: "fm-dialog pff-dialog" }}
    >
      <div className="fm-header">
        <span className="fm-header-icon" aria-hidden>
          <Layers size={20} strokeWidth={2} />
        </span>
        <div className="fm-header-copy">
          <h2>Update plan feature</h2>
          <p>
            Adjust pricing and status for{" "}
            <strong>{displayName}</strong>
            {formik.values.portal_name
              ? ` on ${formik.values.portal_name}`
              : ""}
            .
          </p>
        </div>
        <IconButton
          aria-label="close"
          className="fm-close"
          onClick={handleClose}
          size="small"
        >
          <X size={18} strokeWidth={2} />
        </IconButton>
      </div>

      {fetching ? (
        <div className="pff-loading">
          <CircularProgress size={36} sx={{ color: "#22419e" }} />
          <p>Loading feature details…</p>
        </div>
      ) : (
        <form onSubmit={formik.handleSubmit} className="fm-form">
          <div className="fm-body">
            <section className="fm-section">
              <div className="fm-section-head">
                <h3>Main feature</h3>
                <p>Core details and pricing for this plan feature.</p>
              </div>
              <div className="fm-card">
                <div className="fm-grid">
                  <div className="fm-field">
                    <label htmlFor="pff-portal">Portal</label>
                    <TextField
                      id="pff-portal"
                      name="portal_name"
                      size="small"
                      fullWidth
                      disabled
                      value={formik.values.portal_name}
                      inputProps={{ style: { textTransform: "capitalize" } }}
                    />
                  </div>

                  <div className="fm-field">
                    <label htmlFor="pff-feature-name">
                      Feature name<span className="fm-req">*</span>
                    </label>
                    <TextField
                      id="pff-feature-name"
                      disabled
                      placeholder="Enter feature name"
                      name="feature_name"
                      size="small"
                      fullWidth
                      value={formik.values.name}
                      onChange={handleTextChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.feature_name &&
                        Boolean(formik.errors.feature_name)
                      }
                      helperText={
                        formik.touched.feature_name && formik.errors.feature_name
                      }
                    />
                  </div>

                  <div className="fm-field">
                    <label htmlFor="pff-price">
                      Price<span className="fm-req">*</span>
                    </label>
                    <TextField
                      id="pff-price"
                      placeholder="0.00"
                      name="price"
                      type="number"
                      size="small"
                      fullWidth
                      value={formik.values.price}
                      onChange={handleTextChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.price && Boolean(formik.errors.price)}
                      helperText={formik.touched.price && formik.errors.price}
                      inputProps={{ min: 0, step: "0.01" }}
                    />
                  </div>

                  <div className="fm-field is-full">
                    <label
                      className={`fm-toggle${
                        formik.values.is_active ? " is-on" : ""
                      }`}
                    >
                      <span>
                        <p className="fm-toggle-title">Active status</p>
                        <p className="fm-toggle-sub">
                          Inactive features are hidden from new plan
                          configurations.
                        </p>
                      </span>
                      <Switch
                        checked={formik.values.is_active}
                        onChange={(e) =>
                          formik.setFieldValue("is_active", e.target.checked)
                        }
                        inputProps={{ "aria-label": "Active status" }}
                        sx={switchSx}
                      />
                    </label>
                  </div>
                </div>
              </div>
            </section>

            <section className="fm-section">
              <div className="fm-section-head">
                <h3>Add-on features</h3>
                <p>
                  Nested options under this feature
                  {formik.values.sub_features.length
                    ? ` · ${formik.values.sub_features.length} item${
                        formik.values.sub_features.length === 1 ? "" : "s"
                      }`
                    : ""}
                  .
                </p>
              </div>

              {formik.values.sub_features.length === 0 ? (
                <div className="fm-card pff-empty">
                  <p>No add-on features exist under this parent feature.</p>
                </div>
              ) : (
                <div className="pff-sub-list">
                  {formik.values.sub_features.map((sub, index) => (
                    <div
                      key={sub.id || index}
                      className="pff-sub-card"
                      style={{
                        marginLeft: Math.max(0, (sub.level - 2) * 16),
                      }}
                    >
                      <div className="pff-sub-grid">
                        <div className="fm-field">
                          <div className="pff-field-label-row">
                            <label htmlFor={`pff-sub-name-${index}`}>Name</label>
                            <span className="pff-level-chip">
                              L{sub.level ?? "—"}
                            </span>
                          </div>
                          <TextField
                            id={`pff-sub-name-${index}`}
                            size="small"
                            fullWidth
                            value={sub.name}
                            onChange={(e) =>
                              handleSubFeatureChange(
                                index,
                                "feature_name",
                                e.target.value
                              )
                            }
                            error={
                              formik.touched?.sub_features?.[index]
                                ?.feature_name &&
                              Boolean(
                                formik.errors?.sub_features?.[index]
                                  ?.feature_name
                              )
                            }
                            helperText={
                              formik.touched?.sub_features?.[index]
                                ?.feature_name &&
                              formik.errors?.sub_features?.[index]?.feature_name
                            }
                          />
                        </div>

                        <div className="fm-field">
                          <label htmlFor={`pff-sub-price-${index}`}>Price</label>
                          <TextField
                            id={`pff-sub-price-${index}`}
                            type="number"
                            size="small"
                            fullWidth
                            value={sub.price}
                            onChange={(e) =>
                              handleSubFeatureChange(
                                index,
                                "price",
                                e.target.value
                              )
                            }
                            inputProps={{ min: 0, step: "0.01" }}
                          />
                        </div>

                        <div className="fm-field pff-sub-active">
                          <label className="pff-active-label" htmlFor={`pff-sub-active-${index}`}>
                            Active
                          </label>
                          <div
                            className={`pff-active-control${
                              sub.is_active ? " is-on" : ""
                            }`}
                          >
                            <Switch
                              id={`pff-sub-active-${index}`}
                              checked={sub.is_active}
                              onChange={(e) =>
                                handleSubFeatureChange(
                                  index,
                                  "is_active",
                                  e.target.checked
                                )
                              }
                              size="small"
                              inputProps={{
                                "aria-label": `Active status for ${sub.name || "add-on"}`,
                              }}
                              sx={switchSx}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {typeof formik.errors.sub_features === "string" && (
                <p className="pff-error">{formik.errors.sub_features}</p>
              )}
            </section>
          </div>

          <div className="fm-footer">
            <button
              type="button"
              className="fm-btn"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="fm-btn is-primary"
              disabled={loading || fetching}
            >
              {loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "Update"
              )}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
};

export default AddEditPlanFeature;
